import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CreditCard, ExternalLink, FileText, Receipt } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import SarSymbol from '@/components/SarSymbol';
import { VatIncludedLabel } from '@/components/billing/PricingBreakdown';
import { formatLocalizedDate } from '@/lib/localizedDate';
import type { BillingHistoryEntry, BillingHistoryInvoice } from '@/lib/billing/billingHistory';

type BillingHistoryBrand = 'forkhire' | 'forkseek';

interface BillingHistoryCardProps {
  entries: BillingHistoryEntry[];
  brand: BillingHistoryBrand;
  loading?: boolean;
  error?: boolean;
  summary?: boolean;
  viewAllTo?: string;
  retryLabel: string;
  errorTitle: string;
  errorMessage: string;
  onRetry: () => void;
  onOpenInvoice: (invoice: BillingHistoryInvoice) => void;
}

function translateWithFallback(t: ReturnType<typeof useTranslation>['t'], key: string, fallback: string) {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

function formatPaymentType(t: ReturnType<typeof useTranslation>['t'], paymentType?: string | null) {
  const normalized = paymentType || 'payment';
  return translateWithFallback(t, `billingHistory.paymentTypes.${normalized}`, normalized.replace(/_/g, ' '));
}

function formatPaymentStatus(t: ReturnType<typeof useTranslation>['t'], status?: string | null) {
  const normalized = status || 'unknown';
  return translateWithFallback(t, `billingHistory.statuses.${normalized}`, normalized.replace(/_/g, ' '));
}

function getStatusClass(status?: string | null) {
  if (status === 'paid' || status === 'succeeded') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-0';
  if (status === 'failed' || status === 'payment_failed' || status === 'cancelled' || status === 'expired') return 'bg-destructive text-destructive-foreground border-0';
  if (status === 'refunded') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-0';
  return '';
}

function formatInvoiceReference(invoice: BillingHistoryInvoice | null) {
  return invoice?.erp_invoice_map?.find?.((map) => !!map.external_invoice_number)?.external_invoice_number
    ?? invoice?.invoice_number
    ?? null;
}

export function BillingHistoryCard({
  entries,
  brand,
  loading = false,
  error = false,
  summary = false,
  viewAllTo,
  retryLabel,
  errorTitle,
  errorMessage,
  onRetry,
  onOpenInvoice,
}: BillingHistoryCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const accent = brand === 'forkseek' ? 'text-seek' : 'text-primary';
  const hover = brand === 'forkseek' ? 'hover:bg-seek/5' : 'hover:bg-muted/40';
  const focus = brand === 'forkseek' ? 'focus-visible:ring-seek' : 'focus-visible:ring-primary';

  return (
    <Card className="overflow-hidden rounded-xl border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-3 px-5 py-4 sm:px-6">
        <CardTitle className="flex min-w-0 items-center gap-2 text-base font-semibold">
          <Receipt className={`h-5 w-5 shrink-0 ${accent}`} />
          <span className="truncate">{t('billingHistory.title')}</span>
        </CardTitle>
        {viewAllTo ? (
          <Button asChild variant="outline" size="sm" className="shrink-0 rounded-xl">
            <Link to={viewAllTo} aria-label={t('billingHistory.viewAllAria')}>
              {t('common.viewAll')}
              <ExternalLink className="ms-1.5 h-3.5 w-3.5 rtl-flip" />
            </Link>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-0 sm:px-6">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : error ? (
          <Alert variant="destructive" className="border-destructive/40">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorTitle}</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{errorMessage}</p>
              <Button type="button" size="sm" variant="outline" onClick={onRetry}>
                {retryLabel}
              </Button>
            </AlertDescription>
          </Alert>
        ) : entries.length === 0 ? (
          <p className="py-5 text-center text-sm font-medium text-muted-foreground">{t('billingHistory.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {entries.map((entry) => {
              const invoiceReference = formatInvoiceReference(entry.invoice);
              const grossTotal = entry.payment.total_sar;
              const hasProvenGrossTotal = grossTotal != null && Number.isFinite(Number(grossTotal));
              return (
                <div
                  key={entry.payment.id}
                  className={`grid gap-3 py-3 text-sm transition-colors sm:grid-cols-[minmax(0,1.5fr)_auto_auto] sm:items-center ${hover}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{formatPaymentType(t, entry.payment.payment_type)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {entry.payment.created_at ? formatLocalizedDate(entry.payment.created_at, summary ? 'MMM dd, yyyy' : 'PPp', language) : t('common.notAvailable')}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {hasProvenGrossTotal ? (
                      <span className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-foreground">
                        <SarSymbol size={11} />
                        {grossTotal}
                        <VatIncludedLabel className="ms-1" />
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-muted-foreground">{t('common.notAvailable')}</span>
                    )}
                    <Badge variant={getStatusClass(entry.payment.status) ? 'outline' : 'secondary'} className={getStatusClass(entry.payment.status)}>
                      {formatPaymentStatus(t, entry.payment.status)}
                    </Badge>
                  </div>

                  <div className="flex min-w-0 justify-start sm:justify-end">
                    {entry.invoiceState === 'available' && entry.invoice ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={`max-w-full gap-1.5 rounded-xl ${focus}`}
                        aria-label={t('billingHistory.viewInvoiceAria', { reference: invoiceReference ?? entry.invoice.id })}
                        onClick={() => onOpenInvoice(entry.invoice!)}
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{t('billingHistory.viewInvoice')}</span>
                      </Button>
                    ) : entry.invoiceState === 'processing' ? (
                      <Badge variant="secondary" className="max-w-full truncate">{t('billingHistory.invoiceProcessing')}</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('billingHistory.noInvoiceDocument')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
